<template>

  <!-- Modal -->
  <div v-if="isModalOpen" class="modal-overlay" @click="closeModal">
    <div class="modal-content card" @click.stop>
      <button class="btn-close text-dark" @click="closeModal"></button>

    
      <div class="row">
        <!-- Primera columna: Icono grande -->
        <div class="col-lg-2 col-md-12 d-flex justify-content-center align-items-center">
          <i class="fas fa-file-import text-dark icon-large"></i>
        </div>

        <!-- Segunda columna: Contenido -->
        <div class="col-lg-10 col-md-12 pe-2 pe-lg-4">
          <div class="py-2 px-0 card-header">
            <div class="d-flex title justify-content-between pb-2">
              <h4 class="mb-2">Cargar Padron Electoral TSE</h4>
            </div>
            <div class="d-flex justify-content-start align-items-center">
              <p class="mb-3 text-sm">
                Seleccione un archivo para cargar desde su computadora o dispositivo.
              </p>
            </div>
          </div>

          <!-- Área de arrastrar y soltar -->
          <div
            class="drop-area"
            :class="{ 'drag-over': dragOver }"
            @dragover.prevent="dragOver = true"
            @dragleave="dragOver = false"
            @drop="handleDrop"
            @click="triggerFileInput"
            :style="{ pointerEvents: isLoading ? 'none' : 'auto' }" 
          >
            <p class="mb-0" v-if="!selectedFile && !isLoading">Arrastre y suelte su archivo aquí</p>
            <p class="mb-0" v-else-if="selectedFile && !isLoading">Archivo seleccionado: {{ selectedFile.name }}</p>

            <!-- Mostrar el spinner cuando está cargando -->
            <div v-if="isLoading" class="spinner-container">
              <div class="spinner-border text-text-dark" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>
          </div>

          <!-- Ocultamos el input de archivo ya que se activará al hacer clic en la zona de arrastre -->
          <input type="file" ref="fileInput" @change="handleFileChange" style="display: none;" />
          <div class="d-flex justify-content-end align-items-center">
            <p v-if="errorMessage" class="text-danger mb-4 text-xs pe-2 pe-md-7">{{ errorMessage }}</p>
            <p class="mb-4 text-xs">
              *Formato admitido: <strong>.txt</strong>
            </p>
          </div>
          <!-- Botones de acción -->
          <div class="d-flex mt-4 justify-content-end align-items-center">
            <button class="btn d-flex mb-0 me-2 me-md-4" @click="uploadFile" :disabled="isLoading">
              <i class="text-sm fas fa-upload text-dark pe-2"></i>
              <span v-if="isLoading">Cargando...</span>
              <span v-else>Cargar</span>
            </button>
            <button class="btn d-flex mb-0 " @click="closeModal">
              <i class="text-sm fas fa-times text-secondary pe-2"></i>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import { ref } from "vue";
import axios from "axios";
import Swal from "sweetalert2"; // Import SweetAlert2
import { BACK_END_URL } from '@/utilities/config';

const isModalOpen = ref(false);

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};

const selectedFile = ref(null);
const dragOver = ref(false);
const isLoading = ref(false);
const errorMessage = ref("");


defineExpose({
  openModal,
  closeModal
});

const handleDrop = (event) => {
  event.preventDefault();
  dragOver.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
};

const triggerFileInput = () => {
  const fileInput = document.querySelector('input[type="file"]');
  fileInput?.click();
};

const handleFileChange = (event) => {
  selectedFile.value = event.target.files[0];
};

const uploadFile = async () => {
  if (!selectedFile.value) {
    errorMessage.value = "Por favor, seleccione un archivo antes de subirlo.";
    return;
  }

  // Validar que el archivo sea un .txt
  if (!selectedFile.value.name.endsWith(".txt")) {
    errorMessage.value = "Solo se permiten archivos .txt.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = ""; // Limpiar errores previos

  const file = selectedFile.value;
  const chunkSize = 2 * 1024 * 1024; // 2MB por chunk
  const totalChunks = Math.ceil(file.size / chunkSize);
  const fileId = generateUniqueId(); // ID único del archivo
  const token = sessionStorage.getItem("token");

  try {
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("fileName", file.name);
      formData.append("fileId", fileId);
      formData.append("chunkIndex", i);
      formData.append("totalChunks", totalChunks);

      await axios.post(`${BACK_END_URL}/analytics/upload`, formData, {
        timeout: 0,
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}`,
        },
      });

      console.log(`✅ Chunk ${i + 1}/${totalChunks} enviado.`);
    }

    Swal.fire({
      title: "¡Éxito!",
      text: "Archivo subido exitosamente. Los cambios se reflejarán en breve.",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      location.reload(); // Refrescar la página después de la subida
    });

    closeModal();
  } catch (error) {
    closeModal();
    console.error("Error al subir el archivo:", error);
    Swal.fire("Error", "No se pudo subir el archivo.", "error");
  } finally {
    isLoading.value = false;
  }
};

 
// 🆔 Función para generar un ID único para el archivo
function generateUniqueId() {
  return Date.now() + Math.random().toString(36).substring(2, 15);
}
</script>

<style scoped>
/* Fondo del modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  /* Ensures it covers the whole screen */
  width: 100vw;
  height: 100vh;
  background: rgba(0, 24, 6, 0.384);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  /* Ensure it appears on top */
}


/* Contenedor del modal */
.modal-content {
  background: white;
  padding: 20px;
  border-radius: 15px;
  width: 45%;
  text-align: center;
  display: flex;
  flex-direction: column;
  overflow: hidden;

}


/* Icono grande */
.icon-large {
  font-size: 4rem;
  color: #2e2f30;
}

/* Área de arrastrar y soltar */
.drop-area {
  border: 2px dashed #ccc;
  padding: 40px;
  margin: 20px 0;
  cursor: pointer;
  border-radius: 10px;
  position: relative;
  /* Needed for positioning the spinner */
}

.drop-area:hover {
  border-color: #2dce89;
  background: #e7eaed;
}

.drag-over {
  border-color: #2dce89;
  background: #e7eaed;
}

@media (max-width: 1200px) {
  .modal-overlay {
    width: 100%;
  }

  .modal-content {
    width: 400px;
    max-width: 400px;
    padding: 15px;
    margin: 5%;
    max-height: 85vh;
    padding-bottom: 25px;
  }

  .modal-content h6,
  .modal-content p {
    font-size: 12px !important;
  }

  .modal-content .btn {
    font-size: 12px;
    padding: 8px 12px;
    margin-top: 0%;
  }

  .modal-content .d-flex {
    align-items: flex-start;
  }

  .no-wrap-mobile {
    flex-wrap: nowrap !important;
  }

  h4 {
    justify-content: center !important;
    font-size: 1rem !important;
  }

  .icon-large {
    font-size: 2rem !important;
  }

  .fixed-plugin .fixed-plugin-button {
    right: 70% !important;
  }

  .fixed-plugin .fixed-plugin-button {
    bottom: 30px;
    right: 95% !important;
    z-index: 1050;
  }

}


/* Spinner en la zona de arrastre */
.spinner-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Botones */
.modal-buttons {
  display: flex;
  justify-content: space-around;
  margin-top: 10px;
}

.modal-buttons button {
  padding: 8px 12px;
  border: none;
  cursor: pointer;
}

.modal-buttons button:first-child {
  background: #28a745;
  color: white;
}

.modal-buttons button:last-child {
  background: #dc3545;
  color: white;
}
</style>
